import { env } from '@/lib/env'

import type { $ZodCatchCtx } from 'zod/v4/core'

// Special function that returns a caller source from the stack trace
const getSourceName = (): string => {
  try {
    const stack = new Error('zod-catch').stack ?? ''
    const lines = stack
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)

    const ignore = ['defaultZodCatch', 'node_modules/zod']
    const callerLine = lines.find((line) => {
      const isIgnored = ignore.some((h) => line.includes(h))
      const looksLikePath = /[\\/]|https?:/.test(line)

      return !isIgnored && looksLikePath
    })
    if (!callerLine) {
      return import.meta.url.split('/').pop() ?? 'unknown'
    }

    const match = callerLine.match(/\((.*?):\d+:\d+\)|at (.*?):\d+:\d+$/)
    const fullPath = (match?.[1] ?? match?.[2]) as string | undefined
    if (!fullPath) {
      return import.meta.url.split('/').pop() ?? 'unknown'
    }

    const lastSlash = Math.max(fullPath.lastIndexOf('/'), fullPath.lastIndexOf('\\'))

    return lastSlash >= 0 ? fullPath.slice(lastSlash + 1) : fullPath
  } catch {
    return import.meta.url.split('/').pop() ?? 'unknown'
  }
}

export const defaultZodCatch = <T>(ctx: $ZodCatchCtx) => {
  // Turn off in production
  if (env.AZ_ENV !== 'p') {
    const sourceName = getSourceName()

    const errors = ctx.error.issues.map((err) => {
      const path = err.path.length > 0 ? err.path.join('/') : 'N/A'

      const expected = 'expected' in err ? ` Expected: ${String(err.expected)}` : ''
      const received = 'received' in err ? ` Received: ${String(err.input)}` : ''

      return `Path: ${path} Message: ${err.message} [${err.code}]${expected}${received}`
    })

    console.groupCollapsed(`Error: Invalid API response for ${sourceName}:\n${errors.join('\n')}`)
    console.trace(ctx)
    console.groupEnd()
  }

  // Return the original input data to not break the application
  return ctx.value as T
}
